import { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Lock, CheckCircle, AlertCircle, Eye, EyeOff } from '../icons/wp-icons';
import apiClient from '../api/apiClient';
import { getHostBranding, applyHostBrandingMeta } from '../utils/hostBranding';
import HostBrandedNotice from '../components/HostBrandedNotice';

function ResetPassword() {
  const hostBranding = useMemo(() => getHostBranding(), []);
  const isHeykikiHost = !!hostBranding;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // Theme-Color + Document-Title pro Host setzen — gleiches Pattern wie Login.
  useEffect(() => applyHostBrandingMeta(hostBranding, 'Passwort setzen'), [hostBranding]);

  // Akzentfarben + Logo + Fonts aus dem Host-Branding ziehen, sonst WerkPilot-Defaults.
  const accent = hostBranding?.accent || '#378ab2';
  const accentDark = hostBranding?.accentDark || '#2a6d8a';
  const logoSrc = hostBranding?.logo || '/icons/icon-512x512.png';
  const displayName = hostBranding?.name || 'WerkPilot';
  const fontFamily = hostBranding?.fontFamily || "'Montserrat', sans-serif";

  // Auf Whitelabel-Hosts war der Passwort-Reset historisch extern verwaltet
  // (HeyKiki-Auth lief mal über N8N). Inzwischen verschicken wir Mitarbeiter-
  // Welcome-Mails selbst mit Reset-Token, der Flow MUSS also durchlaufen wenn
  // ein gültiger Token in der URL steckt. Der „extern verwaltet"-Hinweis
  // greift nur noch, wenn jemand /reset-password direkt ohne Token aufruft.
  if (hostBranding?.passwordFlowHandledExternally && !token) {
    return (
      <HostBrandedNotice
        hostBranding={hostBranding}
        pageTitle="Passwort zurücksetzen"
        title="Passwort-Reset"
        message={`Passwörter werden bei ${hostBranding.supportContact} verwaltet. Bitte wende dich für das Zurücksetzen deines Passworts direkt an den ${hostBranding.supportContact}-Support.`}
      />
    );
  }

  // Token validieren beim Laden
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setValidating(false);
        setTokenValid(false);
        return;
      }

      try {
        const response = await apiClient.get(`/auth/validate-reset-token?token=${token}`);
        setTokenValid(response.data.valid);
      } catch (err) {
        setTokenValid(false);
      } finally {
        setValidating(false);
      }
    };

    validateToken();
  }, [token]);

  const getPasswordStrength = (pwd) => {
    if (!pwd) return { strength: 0, label: '', color: '' };

    let strength = 0;
    if (pwd.length >= 8) strength++;
    if (pwd.length >= 12) strength++;
    if (/[a-z]/.test(pwd) && /[A-Z]/.test(pwd)) strength++;
    if (/\d/.test(pwd)) strength++;
    if (/[^a-zA-Z0-9]/.test(pwd)) strength++;

    const levels = [
      { label: 'Sehr schwach', color: 'bg-red-500' },
      { label: 'Schwach', color: 'bg-orange-500' },
      { label: 'Mittel', color: 'bg-yellow-500' },
      { label: 'Stark', color: 'bg-lime-500' },
      { label: 'Sehr stark', color: 'bg-green-500' },
    ];

    return { strength, ...levels[Math.min(strength, 4)] };
  };

  const passwordStrength = getPasswordStrength(password);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validierung
    if (password.length < 8) {
      setError('Das Passwort muss mindestens 8 Zeichen lang sein.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Die Passwörter stimmen nicht überein.');
      return;
    }

    setLoading(true);

    try {
      await apiClient.post('/auth/reset-password', { token, password });
      setSuccess(true);

      // Nach 3 Sekunden zur Login-Seite weiterleiten
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Loading-Zustand beim Validieren
  if (validating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="bg-surface p-8 rounded-card shadow-card w-full max-w-md text-center">
          <div
            className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4"
            style={{ borderBottomColor: accent }}
          ></div>
          <p className="text-text-muted">Link wird geprüft...</p>
        </div>
      </div>
    );
  }

  // Token ungültig oder fehlt
  if (!tokenValid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="bg-surface p-8 rounded-card shadow-card w-full max-w-md text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>

          <h2 className="text-xl font-bold text-gray-900 mb-2" style={{ fontFamily }}>
            Link ungültig
          </h2>

          <p className="text-text-muted mb-6">
            {!token
              ? 'Es wurde kein Reset-Link angegeben.'
              : 'Der Link zum Zurücksetzen des Passworts ist ungültig oder abgelaufen.'}
          </p>

          <Link
            to="/forgot-password"
            className="inline-block w-full text-white py-3 rounded-button font-medium transition text-center mb-4"
            style={{ backgroundColor: accent }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = accentDark; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = accent; }}
          >
            Neuen Link anfordern
          </Link>

          <Link
            to="/login"
            className="inline-flex items-center justify-center gap-2 hover:underline font-medium"
            style={{ color: accent }}
          >
            <ArrowLeft className="w-4 h-4" />
            Zurück zur Anmeldung
          </Link>
        </div>
      </div>
    );
  }

  // Erfolg
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="bg-surface p-8 rounded-card shadow-card w-full max-w-md text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>

          <h2 className="text-xl font-bold text-gray-900 mb-2" style={{ fontFamily }}>
            Passwort geändert
          </h2>

          <p className="text-text-muted mb-6">
            Ihr Passwort wurde erfolgreich geändert. Sie werden in wenigen Sekunden zur Anmeldung weitergeleitet.
          </p>

          <Link
            to="/login"
            className="inline-block w-full text-white py-3 rounded-button font-medium transition text-center"
            style={{ backgroundColor: accent }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = accentDark; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = accent; }}
          >
            Jetzt anmelden
          </Link>
        </div>
      </div>
    );
  }

  // Formular zum Passwort ändern
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="bg-surface p-8 rounded-card shadow-card w-full max-w-md">
        <div className="text-center mb-6">
          {isHeykikiHost && logoFailed ? (
            <div
              className="w-24 h-24 mx-auto mb-4 rounded-full flex items-center justify-center"
              style={{ backgroundColor: accent }}
            >
              <span className="text-white text-4xl font-bold" style={{ fontFamily }}>
                {displayName.substring(0, 2)}
              </span>
            </div>
          ) : (
            <img
              src={logoSrc}
              alt={`${displayName} Logo`}
              className="w-24 h-24 mx-auto mb-4 object-contain"
              onError={() => setLogoFailed(true)}
            />
          )}
          <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily }}>
            Neues Passwort festlegen
          </h1>
          <p className="text-text-muted mt-2">
            Bitte wählen Sie ein sicheres Passwort mit mindestens 8 Zeichen.
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Neues Passwort</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 pl-11 pr-11 border border-border rounded-lg focus:outline-none focus:ring-2"
                style={{ '--tw-ring-color': accent }}
                placeholder="Mindestens 8 Zeichen"
                required
                disabled={loading}
                autoFocus
                minLength={8}
              />
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>

            {/* Passwort-Stärke-Anzeige */}
            {password && (
              <div className="mt-2">
                <div className="flex gap-1 mb-1">
                  {[...Array(5)].map((_, i) => (
                    <div
                      key={i}
                      className={`h-1 flex-1 rounded-full ${
                        i < passwordStrength.strength ? passwordStrength.color : 'bg-gray-200'
                      }`}
                    />
                  ))}
                </div>
                <p className="text-xs text-text-muted">
                  Passwortstärke: <span className="font-medium">{passwordStrength.label}</span>
                </p>
              </div>
            )}
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium mb-2">Passwort bestätigen</label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={`w-full px-4 py-3 pl-11 pr-11 border rounded-lg focus:outline-none focus:ring-2 ${
                  confirmPassword && password !== confirmPassword
                    ? 'border-red-300 bg-red-50'
                    : confirmPassword && password === confirmPassword
                    ? 'border-green-300 bg-green-50'
                    : 'border-border'
                }`}
                style={{ '--tw-ring-color': accent }}
                placeholder="Passwort wiederholen"
                required
                disabled={loading}
                minLength={8}
              />
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {confirmPassword && password !== confirmPassword && (
              <p className="text-xs text-red-600 mt-1">Die Passwörter stimmen nicht überein</p>
            )}
            {confirmPassword && password === confirmPassword && (
              <p className="text-xs text-green-600 mt-1">Passwörter stimmen überein</p>
            )}
          </div>

          <button
            type="submit"
            className="w-full text-white py-3 rounded-button font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: accent }}
            onMouseEnter={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.backgroundColor = accentDark; }}
            onMouseLeave={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.backgroundColor = accent; }}
            disabled={loading || password.length < 8 || password !== confirmPassword}
          >
            {loading ? 'Wird gespeichert...' : 'Passwort ändern'}
          </button>
        </form>

        <div className="mt-6 pt-6 border-t border-border text-center">
          <Link
            to="/login"
            className="inline-flex items-center gap-2 hover:underline font-medium"
            style={{ color: accent }}
          >
            <ArrowLeft className="w-4 h-4" />
            Zurück zur Anmeldung
          </Link>
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;
